/**
 * ПРИБОР «ГЛАЗАМИ РЕКТОРА» — разметка экранов админки (программа «Пульт», строка 1.1).
 *
 * `PanelViewProvider` стоит в админке над вкладками и начинает открытие на каждой смене адреса страницы.
 * Экран ставит метку «данные видны» одной строкой: `useDataVisible(готово, не пришло)` — в момент, когда главное,
 * ради чего вкладку открывают, нарисовано (список чатов, лента чата, таблица заказов), или когда показана ошибка
 * загрузки. Новый экран без метки — дефект разметки (маяк уходит с `marks: 0`, тест вкладок не пропускает).
 *
 * Метка привязана к открытию, в котором экран смонтирован: компонент, переживший смену адреса, не отмечает чужое
 * открытие устаревшими данными. Вне админки (кабинет человека, тот же экран рефералов) прибора нет — метка ничего
 * не делает.
 */
import {
  createContext,
  type ReactNode,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getActiveEdge, servedBridge } from '../lib/edge-fetch';
import { browserSurroundings, type PanelViewTracker, panelViewTracker } from '../lib/panel-view';

interface PanelViewContextValue {
  tracker: PanelViewTracker;
  viewId: number | null;
}

const PanelViewContext = createContext<PanelViewContextValue | null>(null);

export function PanelViewProvider({ apiBase, children }: { apiBase: string; children: ReactNode }) {
  const location = useLocation();
  const tracker = useMemo(
    () =>
      panelViewTracker(apiBase, () =>
        browserSurroundings({
          bridge: servedBridge(),
          own: getActiveEdge(window.location.origin) === window.location.origin,
        })
      ),
    [apiBase]
  );
  // Открытие начинается в той же отрисовке, что и смена адреса (переход синхронный — это и есть момент нажатия):
  // экран нового адреса уже видит его номер. Повтор с тем же ключом навигации — то же открытие.
  const viewId = tracker.begin(location.key, location.pathname);
  const value = useMemo(() => ({ tracker, viewId }), [tracker, viewId]);
  return <PanelViewContext.Provider value={value}>{children}</PanelViewContext.Provider>;
}

/**
 * Метка «данные видны» экрана: `ready` — главное нарисовано, `failed` — показана ошибка загрузки.
 * Ставится на каждом экране админки (одна строка рядом с загрузкой его данных).
 */
export function useDataVisible(ready: boolean, failed = false): void {
  const ctx = useContext(PanelViewContext);
  const [viewId] = useState(() => ctx?.viewId ?? null);
  const hookId = useId();
  const tracker = ctx?.tracker ?? null;
  useLayoutEffect(() => {
    if (!tracker || viewId === null) return undefined;
    tracker.register(viewId, hookId);
    return () => tracker.unregister(viewId, hookId);
  }, [tracker, viewId, hookId]);
  useLayoutEffect(() => {
    if (!tracker || viewId === null) return;
    if (failed) tracker.report(viewId, hookId, 'failed');
    else if (ready) tracker.report(viewId, hookId, 'ready');
  }, [tracker, viewId, hookId, ready, failed]);
}

/** Состояние запросов react-query → метка: готово, когда все пришли; не пришло, когда хоть один упал. */
export function queriesVisible(
  ...qs: Array<{ isSuccess: boolean; isError: boolean }>
): [boolean, boolean] {
  return [qs.every((q) => q.isSuccess), qs.some((q) => q.isError)];
}
